import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { PageHeader, Button, Badge } from "@/components/ui/PageHeader";
import { AssistancePreview } from "./AssistancePreview";
import {
  classify,
  submit,
  ACTION_LABELS,
  type ClassifyResponse,
  type DispatchOutcome,
  type Sensitivity,
  getDemoCases,
  type DemoCase,
} from "@/api/composer";

/**
 * The one box, with its workings showing.
 *
 * A consumer client hides the classification — you type, you tap post, it
 * lands somewhere sensible. That is the product. This is the Lab, so it does
 * the opposite: every value the classifier returned is on screen next to the
 * sentence it produced, because the thing being tested is whether the ruling
 * is *right*, not whether the box submits.
 *
 * Two calls, deliberately separate, mirroring the backend: classify is the
 * reflect-back and posts nothing; submit re-classifies server-side and
 * executes. The read shown here is never sent back as an input the server
 * trusts — a client is not a place to store a crisis flag.
 */

/** Real asks, so a tester can hit every branch without inventing prose. */
const SAMPLES: { label: string; text: string }[] = [
  {
    label: "→ Question",
    text: "We will be moving to the Dallas area from NJ and are hoping to find an area close to the Indian community with good public schools. Can anyone recommend areas?",
  },
  {
    // Was the "→ Question" sample until the first Lab drive showed it falling
    // through unclassified — the fix routes it to providers, which is right:
    // it is a hire, not a discussion.
    label: "→ Providers (pandit)",
    text: "Looking for a good pandit in Fremont for a griha pravesh next month. Someone who explains the steps as they go would be ideal.",
  },
  {
    label: "→ Providers (nanny)",
    text: "Need a part time nanny in Edison who can also help with cooking and school pick up for our 6 year old.",
  },
  {
    label: "→ Voice",
    text: "The new parking rules near the temple in Sunnyvale are making it impossible to attend Sunday aarti. Has anyone else noticed this?",
  },
  {
    label: "→ Decline (matchmaking)",
    text: "My son is 28, works in tech, we are looking for a suitable match from a good Gujarati family in the Bay Area.",
  },
  {
    // Trips the deterministic sensitive sweep ("afraid of my husband",
    // "tracks my location") rather than merely reading as distressing. An
    // upsetting sentence that does NOT contain a pattern comes back `none` on
    // purpose — the sweep is narrow so it doesn't patronise ordinary venting,
    // and a sample that implied otherwise would teach the wrong lesson here.
    label: "→ Sensitive",
    text: "I am afraid of my husband and he tracks my location constantly. I need to know what my options are.",
  },
  {
    // Anonymity forced on, resources returned privately, never broadcast.
    label: "→ Crisis",
    text: "I don't want to live anymore and I have nobody here I can talk to about any of this.",
  },
];

export default function ComposerLab() {
  const [text, setText] = useState("");
  const [reflect, setReflect] = useState<ClassifyResponse | null>(null);
  const [outcome, setOutcome] = useState<DispatchOutcome | null>(null);
  const [anonymous, setAnonymous] = useState(false);
  const [notifyMe, setNotifyMe] = useState(true);
  const [busy, setBusy] = useState<"classify" | "submit" | null>(null);
  const [error, setError] = useState<string | null>(null);

  const tooShort = text.trim().length < 10;
  // Crisis anonymity is not the poster's to switch off — submit re-forces it
  // server-side regardless, so the control says so rather than lying.
  const anonymityLocked = reflect?.sensitivity === "crisis";

  function reset() {
    setReflect(null);
    setOutcome(null);
    setError(null);
  }

  async function runClassify() {
    setBusy("classify");
    reset();
    try {
      const res = await classify(text);
      setReflect(res.data);
      setAnonymous(res.data.suggestedAnonymity);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Classify failed");
    } finally {
      setBusy(null);
    }
  }

  async function runSubmit() {
    setBusy("submit");
    setError(null);
    try {
      const res = await submit({
        text,
        isAnonymous: anonymityLocked ? true : anonymous,
        notifyMe: reflect?.action === "decline" ? notifyMe : undefined,
      });
      setOutcome(res.data);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Submit failed");
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="max-w-3xl">
      <PageHeader
        title="Composer"
        subtitle="One box. The classifier decides what it becomes — and here you can see exactly why."
        breadcrumbs={[{ label: "Lab", href: "/" }, { label: "Composer" }]}
        actions={
          <Link to="/lab/composer/feed">
            <Button variant="secondary" size="sm">
              Community feed
            </Button>
          </Link>
        }
      />

      {/* ── the box ─────────────────────────────────────────────── */}
      <div className="bg-white border border-gray-200 rounded-2xl p-5">
        <textarea
          value={text}
          onChange={(e) => {
            setText(e.target.value);
            if (reflect || outcome) reset();
          }}
          rows={5}
          placeholder="What's going on? Ask for help, share something, raise an issue…"
          className="w-full resize-none border-0 p-0 text-[15px] leading-relaxed text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-0"
        />
        <div className="mt-4 flex items-center justify-between gap-3 border-t border-gray-100 pt-3">
          <span className="text-xs text-gray-400">
            {tooShort
              ? `${Math.max(0, 10 - text.trim().length)} more characters`
              : `${text.trim().length} characters`}
          </span>
          <Button
            onClick={runClassify}
            disabled={tooShort}
            isLoading={busy === "classify"}
            size="sm"
          >
            Classify
          </Button>
        </div>
      </div>

      <AssistancePreview text={text} />

      {/* ── the demo bank (52 curated cases), or the local samples ── */}
      <DemoBank
        onPick={(t) => {
          setText(t);
          reset();
        }}
      />

      {error && (
        <div className="mt-4 rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {/* ── the reflect-back ────────────────────────────────────── */}
      {reflect && !outcome && (
        <section className="mt-6 rounded-2xl border border-gray-200 bg-white p-5">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 className="font-display text-lg font-semibold text-gray-900">
                {ACTION_LABELS[reflect.action] ?? reflect.action}
              </h2>
              {/* The promise, restated in words — half of the two magic bullets. */}
              <p className="mt-1 text-sm text-gray-600">
                {reflect.audienceDescription}
              </p>
            </div>
            <SensitivityChip value={reflect.sensitivity} />
          </div>

          {reflect.suggestedTitle && (
            <p className="mt-3 text-sm text-gray-500">
              Suggested title:{" "}
              <span className="text-gray-900">{reflect.suggestedTitle}</span>
            </p>
          )}

          {/* The raw ruling. This is the Lab's whole reason to exist: the
              sentence above is only trustworthy if these line up with it. */}
          <dl className="mt-4 grid grid-cols-2 gap-x-6 gap-y-2 rounded-xl bg-gray-50 p-4 text-xs sm:grid-cols-3">
            <Field label="action" value={reflect.action} mono />
            <Field label="topic" value={reflect.topic} mono />
            <Field
              label="confidence"
              value={reflect.confidence.toFixed(2)}
              mono
            />
            <Field label="sensitivity" value={reflect.sensitivity} mono />
            <Field
              label="unmetIntent"
              value={reflect.unmetIntent ?? "—"}
              mono
            />
            <Field
              label="submissionId"
              value={reflect.submissionId ? "recorded" : "—"}
            />
          </dl>

          {reflect.fallbackReason && (
            <p className="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800">
              The classifier didn't produce a usable answer, so the
              deterministic fallback ran — <strong>{reflect.fallbackReason}</strong>.
              This is the never-block contract working, not an error.
            </p>
          )}

          {/* ── attribution ─────────────────────────────────────── */}
          <div className="mt-5 border-t border-gray-100 pt-4">
            <label className="flex items-start gap-2.5">
              <input
                type="checkbox"
                checked={anonymityLocked ? true : anonymous}
                disabled={anonymityLocked}
                onChange={(e) => setAnonymous(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-gray-300"
              />
              <span className="text-sm text-gray-700">
                Post anonymously
                {anonymityLocked && (
                  <span className="ml-2 text-xs text-gray-500">
                    — forced on for a crisis post, and re-forced on the server
                    whatever this client sends
                  </span>
                )}
                {!anonymityLocked && reflect.sensitivity === "sensitive" && (
                  <span className="ml-2 text-xs text-gray-500">
                    — defaulted on because this reads as sensitive; you can turn
                    it off
                  </span>
                )}
              </span>
            </label>

            {/* The server's honesty line, rendered verbatim: anonymity strips
                the name, never the identifying details inside the text. */}
            {reflect.anonymityNote && (
              <p className="mt-2 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800">
                {reflect.anonymityNote}
              </p>
            )}

            {reflect.action === "decline" && (
              <label className="mt-3 flex items-start gap-2.5">
                <input
                  type="checkbox"
                  checked={notifyMe}
                  onChange={(e) => setNotifyMe(e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-gray-300"
                />
                <span className="text-sm text-gray-700">
                  Tell me when this launches
                  <span className="ml-2 text-xs text-gray-500">
                    — a decline still logs the demand; this adds you to the
                    waitlist for it
                  </span>
                </span>
              </label>
            )}
          </div>

          <div className="mt-5 flex items-center gap-3">
            <Button onClick={runSubmit} isLoading={busy === "submit"}>
              Submit
            </Button>
            <Button variant="ghost" size="sm" onClick={reset}>
              Discard
            </Button>
          </div>
        </section>
      )}

      {/* ── what actually happened ──────────────────────────────── */}
      {outcome && <Outcome outcome={outcome} onAgain={() => { setText(""); reset(); }} />}
    </div>
  );
}

/* ── pieces ──────────────────────────────────────────────────── */

function Outcome({
  outcome,
  onAgain,
}: {
  outcome: DispatchOutcome;
  onAgain: () => void;
}) {
  const tone =
    outcome.kind === "created"
      ? "green"
      : outcome.kind === "routed"
        ? "blue"
        : "gray";

  return (
    <section className="mt-6 rounded-2xl border border-gray-200 bg-white p-5">
      <div className="flex items-center gap-2">
        <Badge variant={tone}>{outcome.kind}</Badge>
        <span className="text-sm text-gray-500">
          {ACTION_LABELS[outcome.action] ?? outcome.action}
        </span>
      </div>

      {outcome.post && (
        <div className="mt-4">
          <p className="text-sm text-gray-900">
            Posted as a {outcome.post.type}
            {outcome.post.isAnonymous && " — anonymously"}.
          </p>
          <div className="mt-2 flex flex-wrap gap-2">
            <Link
              to={
                outcome.post.type === "voice"
                  ? `/lab/voices/${outcome.post.id}`
                  : `/lab/questions/${outcome.post.id}`
              }
            >
              <Button variant="secondary" size="sm">
                Open the post
              </Button>
            </Link>
            <Link
              to={`/lab/composer/insights/${outcome.post.type}/${outcome.post.id}`}
            >
              <Button variant="secondary" size="sm">
                Insights (OP-only)
              </Button>
            </Link>
          </div>
        </div>
      )}

      {outcome.route && (
        <div className="mt-4">
          <p className="text-sm text-gray-900">
            Routed to <strong>{outcome.route.target}</strong>.
          </p>
          {/* A deep-link contract, not a URL — Android renders this its own
              way, so the Lab shows the contract rather than faking a link. */}
          <pre className="mt-2 overflow-x-auto rounded-lg bg-gray-50 p-3 text-xs text-gray-700">
            {JSON.stringify(outcome.route, null, 2)}
          </pre>
        </div>
      )}

      {outcome.providerSet && (
        <p className="mt-3 text-sm text-gray-700">
          {outcome.providerSet.count} providers in{" "}
          <strong>{outcome.providerSet.categoryCode}</strong>
          {outcome.providerSet.city ? ` near ${outcome.providerSet.city}` : ""} —
          returned as a set, never one ranked name.
        </p>
      )}

      {outcome.existingAnswer && (
        <p className="mt-3 rounded-lg bg-blue-50 px-3 py-2 text-sm text-blue-900">
          Someone already asked this:{" "}
          <strong>{outcome.existingAnswer.title}</strong> (
          {outcome.existingAnswer.answerCount} answers). Surfaced, not blocked.
        </p>
      )}

      {outcome.decline && (
        <div className="mt-4">
          <p className="text-sm text-gray-900">{outcome.decline.message}</p>
          <p className="mt-2 text-xs text-gray-500">
            {outcome.decline.notifyCaptured
              ? "Added to the launch waitlist, and the demand is logged either way."
              : "Demand logged. Not added to the waitlist."}
          </p>
        </div>
      )}

      {outcome.resources && outcome.resources.length > 0 && (
        <div className="mt-4 rounded-xl border border-amber-100 bg-amber-50 p-4">
          <p className="text-xs font-medium uppercase tracking-wide text-amber-900">
            Shown privately to the poster only
          </p>
          <ul className="mt-2 space-y-1">
            {outcome.resources.map((r) => (
              <li key={r.name} className="text-sm text-amber-900">
                {r.name} — <strong>{r.contact}</strong>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="mt-5">
        <Button variant="ghost" size="sm" onClick={onAgain}>
          Compose another
        </Button>
      </div>
    </section>
  );
}

function SensitivityChip({ value }: { value: Sensitivity }) {
  if (value === "crisis") return <Badge variant="red">crisis</Badge>;
  if (value === "sensitive") return <Badge variant="yellow">sensitive</Badge>;
  return <Badge variant="gray">none</Badge>;
}

function Field({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <div>
      <dt className="text-gray-400">{label}</dt>
      <dd className={`text-gray-900 ${mono ? "font-mono" : ""}`}>{value}</dd>
    </div>
  );
}


/**
 * The pre-MVP demo bank: every classifier branch as a tappable chip, grouped
 * by category, fetched from the backend so Lab and Android demo from ONE list.
 * Outside demo-friendly environments the endpoint 403s and the local samples
 * render instead — quietly, because absence is the correct production state.
 * llm_frontier chips are visually marked: they are known stub limitations and
 * demo correctly only on the Anthropic provider.
 */
function DemoBank({ onPick }: { onPick: (text: string) => void }) {
  const { data } = useQuery({
    queryKey: ["composer-demo-cases"],
    queryFn: getDemoCases,
    retry: false,
    staleTime: 5 * 60_000,
  });

  const cases = data?.data.cases;

  if (!cases || cases.length === 0) {
    return (
      <div className="mt-3 flex flex-wrap gap-2">
        {SAMPLES.map((s) => (
          <button
            key={s.label}
            onClick={() => onPick(s.text)}
            className="rounded-full border border-gray-200 bg-white px-3 py-1 text-xs text-gray-600 hover:border-gray-300 hover:text-gray-900"
          >
            {s.label}
          </button>
        ))}
      </div>
    );
  }

  const byCategory = new Map<string, DemoCase[]>();
  for (const c of cases) {
    const list = byCategory.get(c.category) ?? [];
    list.push(c);
    byCategory.set(c.category, list);
  }

  return (
    <div className="mt-3 space-y-2">
      {[...byCategory.entries()].map(([category, items]) => (
        <div key={category} className="flex flex-wrap items-baseline gap-1.5">
          <span className="w-32 shrink-0 text-[10px] font-bold uppercase tracking-wide text-gray-400">
            {category.replace(/_/g, " ")}
          </span>
          {items.map((c) => (
            <button
              key={c.id}
              onClick={() => onPick(c.text)}
              title={[
                `expect: ${c.expectedAction}${c.expectedTopic ? ` / ${c.expectedTopic}` : ""}`,
                c.notes ?? "",
              ]
                .filter(Boolean)
                .join("\n")}
              className={`rounded-full border px-3 py-1 text-xs transition-colors ${
                c.category === "llm_frontier"
                  ? "border-dashed border-amber-300 bg-amber-50 text-amber-800 hover:border-amber-400"
                  : "border-gray-200 bg-white text-gray-600 hover:border-gray-300 hover:text-gray-900"
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>
      ))}
      <p className="text-[10px] text-gray-400">
        Hover a chip for the expected outcome. Amber chips are known stub
        limits — they classify correctly only on the Anthropic provider.
      </p>
    </div>
  );
}
